import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WeighingProvider } from '@/contexts/WeighingContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { ConnectionProvider } from '@/contexts/ConnectionContext';
import Layout from './components/Layout';
import SessionInitializer from './components/SessionInitializer';
import LoginPage from './pages/LoginPage';
import TwoFactorPage from './pages/TwoFactorPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import WeighingPage from './pages/WeighingPage';
import QualityAnalysisPage from './pages/QualityAnalysisPage';
import StockManagementPage from './pages/StockManagementPage';
import NouvellePeseePage from './pages/NouvellePeseePage';
import TicketsPage from './pages/TicketsPage';
import QualityPage from './pages/QualityPage';
import StocksPage from './pages/StocksPage';
import UsersPage from './pages/UsersPage';
import HistoryPage from './pages/HistoryPage';
import StatisticsPage from './pages/StatisticsPage';
import SettingsPage from './pages/SettingsPage';
import ActiveSessionsPage from './pages/ActiveSessionsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AdministrationPage from './pages/AdministrationPage';
import MultiSitePage from './pages/MultiSitePage';
import TenantManagementPage from './pages/TenantManagementPage';
import MultiSiteTenantPage from './pages/MultiSiteTenantPage';
import OfflineManagementPage from './pages/OfflineManagementPage';
import LoadingSpinner from './components/LoadingSpinner';

// Composants offline
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { SyncNotification } from '@/components/OfflineIndicator';
import { SyncModal, useSyncModal } from '@/components/SyncModal';
import { backgroundSyncService } from '@/services/backgroundSyncService';
import ConnectionDebug from '@/components/ConnectionDebug';

import CreateUser from './components/administration/CreateUser';
import UserDetail from './components/administration/UserDetail';
import ApiDiagnostic from './components/administration/ApiDiagnostic';
import SimpleAssignTest from './components/administration/SimpleAssignTest';
import DevConfig from './components/administration/DevConfig';
import UserSessionConfigPage from './pages/UserSessionConfigPage';
import PontsBasculeConfigPage from './pages/PontsBasculeConfigPage';
import PermissionManagementPage from './pages/PermissionManagementPage';
import ErrorBoundary from './components/ErrorBoundary';

// Composant ProtectedRoute déplacé après la définition d'AppContent

const AppContent: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/2fa" element={<TwoFactorPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        
        {/* Protected routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/weighing" element={
          <ProtectedRoute>
            <Layout>
              <WeighingPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/quality-analysis" element={
          <ProtectedRoute>
            <Layout>
              <QualityAnalysisPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/stock-management" element={
          <ProtectedRoute>
            <Layout>
              <StockManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/nouvelle-pesee" element={
          <ProtectedRoute>
            <Layout>
              <NouvellePeseePage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/tickets" element={
          <ProtectedRoute>
            <Layout>
              <TicketsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/quality" element={
          <ProtectedRoute>
            <Layout>
              <QualityPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/stocks" element={
          <ProtectedRoute>
            <Layout>
              <StocksPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/history" element={
          <ProtectedRoute>
            <Layout>
              <HistoryPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/statistics" element={
          <ProtectedRoute>
            <Layout>
              <StatisticsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/active-sessions" element={
          <ProtectedRoute>
            <Layout>
              <ActiveSessionsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/change-password" element={
          <ProtectedRoute>
            <Layout>
              <ChangePasswordPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/administration" element={
          <ProtectedRoute>
            <Layout>
              <AdministrationPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/multi-site" element={
          <ProtectedRoute>
            <Layout>
              <MultiSitePage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/tenant-management" element={
          <ProtectedRoute>
            <Layout>
              <TenantManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/multi-site-tenant" element={
          <ProtectedRoute>
            <Layout>
              <MultiSiteTenantPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* User Management Routes */}
        <Route path="/administration/create-user" element={
          <ProtectedRoute>
            <Layout>
              <CreateUser />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/administration/user/:userId" element={
          <ProtectedRoute>
            <Layout>
              <UserDetail />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/administration/user/:userId/edit" element={
          <ProtectedRoute>
            <Layout>
              <UserDetail />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/administration/user/:userId/sessions" element={
          <ProtectedRoute>
            <Layout>
              <UserSessionConfigPage />
            </Layout>
          </ProtectedRoute>
        } />
       
        <Route path="/administration/api-diagnostic" element={
          <ProtectedRoute>
            <Layout>
              <ApiDiagnostic />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/administration/simple-assign-test" element={
          <ProtectedRoute>
            <Layout>
              <SimpleAssignTest />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/administration/dev-config" element={
          <ProtectedRoute>
            <Layout>
              <DevConfig />
            </Layout>
          </ProtectedRoute>
        } />
       
        
        {/* Configuration des Ponts Bascule */}
        <Route path="/config/ponts-bascule" element={
          <ProtectedRoute>
            <Layout>
              <PontsBasculeConfigPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Gestion des Permissions */}
        <Route path="/permission-management" element={
          <ProtectedRoute>
            <Layout>
              <PermissionManagementPage />
            </Layout>
          </ProtectedRoute>
        } />

        {/* Gestion Offline */}
        <Route path="/offline-management" element={
          <ProtectedRoute>
            <Layout>
              <OfflineManagementPage />
            </Layout>
          </ProtectedRoute>
        } />


            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  const { isOpen, openSyncModal, closeSyncModal, handleSyncComplete } = useSyncModal();

  // Initialiser le service de synchronisation en arrière-plan
  React.useEffect(() => {
    // Démarrer la synchronisation automatique
    backgroundSyncService.start();

    // Écouter les événements de demande de synchronisation
    const handleRequestSync = () => {
      openSyncModal(true);
    };

    const handleOpenOfflineManagement = () => {
      window.location.href = '/offline-management';
    };

    window.addEventListener('request-sync', handleRequestSync);
    window.addEventListener('open-offline-management', handleOpenOfflineManagement);

    return () => {
      window.removeEventListener('request-sync', handleRequestSync);
      window.removeEventListener('open-offline-management', handleOpenOfflineManagement);
    };
  }, [openSyncModal]);

  return (
    <ErrorBoundary>
      <ConnectionProvider>
        <AuthProvider>
          <TenantProvider>
            <WeighingProvider>
              <NotificationProvider>
                <SessionInitializer>
                  {/* Composants offline globaux */}
                  <OfflineIndicator />
                  <SyncNotification />
                  
                  <AppContent />
                  
                  {/* Modal de synchronisation */}
                  <SyncModal
                    isOpen={isOpen}
                    onClose={closeSyncModal}
                    onSyncComplete={handleSyncComplete}
                  />
                  
                  {/* Debug de connexion */}
                  <ConnectionDebug />
                </SessionInitializer>
              </NotificationProvider>
            </WeighingProvider>
          </TenantProvider>
        </AuthProvider>
      </ConnectionProvider>
    </ErrorBoundary>
  );
};

export default App;
